import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, CalendarDays } from "lucide-react";
import { brand } from "../content/siteContent";
import { track, EVENTS } from "../lib/analytics";
import { fireConversion } from "../lib/conversions";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

const labels = {
  otp_cert_full_discount: "Operations Transformation Program — full payment",
  otp_cert_plan_monthly: "Operations Transformation Program — 4-month plan (first instalment)",
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const provider = params.get("provider"); // "paypal" | "paypal_sub" | null (stripe)
  const [state, setState] = useState("polling"); // polling | paid | timeout | error | expired
  const [info, setInfo] = useState(null);

  const confirmPaypal = useCallback(async () => {
    try {
      let data;
      if (provider === "paypal_sub") {
        const subId = params.get("subscription_id");
        if (!subId) { setState("error"); return; }
        ({ data } = await axios.post(`${API}/paypal/subscription/confirm/${subId}`));
      } else {
        const orderId = params.get("token");
        if (!orderId) { setState("error"); return; }
        ({ data } = await axios.post(`${API}/paypal/capture/${orderId}`));
      }
      if (data.payment_status === "paid") {
        setInfo(data);
        setState("paid");
        track("payment_completed", {
          lookup_key: data.lookup_key,
          value: data.amount,
          amount: data.amount,
          currency: data.currency || "aud",
          transaction_id: params.get("subscription_id") || params.get("token") || data.session_id,
          item_name: labels[data.lookup_key] || "Operations Transformation Program enrolment",
          provider: "paypal",
        });
        fireConversion("purchase", {
          value: data.amount,
          currency: (data.currency || "AUD").toUpperCase(),
          transaction_id: params.get("subscription_id") || params.get("token") || data.session_id,
        });
      } else {
        setState("expired");
      }
    } catch (e) {
      setState("error");
    }
  }, [provider, params]);

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setState("error"); return; }
    if (attempt >= MAX_POLLS) { setState("timeout"); return; }
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setInfo(data);
        setState("paid");
        track("payment_completed", {
          lookup_key: data.lookup_key,
          value: data.amount,
          amount: data.amount,
          currency: data.currency || "aud",
          transaction_id: sessionId,
          item_name: labels[data.lookup_key] || "Operations Transformation Program enrolment",
          provider: "stripe",
        });
        fireConversion("purchase", {
          value: data.amount,
          currency: (data.currency || "AUD").toUpperCase(),
          transaction_id: sessionId,
        });
        return;
      }
      if (["expired", "failed"].includes(data.payment_status)) { setState("expired"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch (e) {
      setState("error");
    }
  }, [sessionId]);

  useEffect(() => {
    if (provider === "paypal" || provider === "paypal_sub") {
      confirmPaypal();
    } else {
      poll(0);
    }
  }, [provider, confirmPaypal, poll]);

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center relative overflow-hidden px-6">
      <Seo path="/payment/success" title="Payment | Operations Transformation Program" noindex />
      <div className="absolute inset-0 blueprint opacity-50" />
      <div className="relative w-full max-w-lg text-center">
        {state === "polling" && (
          <div data-testid="payment-polling">
            <Loader2 className="h-10 w-10 text-teal mx-auto animate-spin" />
            <p className="mt-6 text-white/80 text-base">Confirming your payment…</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="payment-paid">
            <CheckCircle2 className="h-14 w-14 text-teal mx-auto" />
            <p className="eyebrow text-gold mt-6">{brand.tagline}</p>
            <h1 className="mt-4 font-display font-extrabold text-white text-3xl lg:text-4xl tracking-tight">
              Your place is secured.
            </h1>
            {info && (
              <p className="mt-4 text-white/70 text-sm">
                {labels[info.lookup_key] || "Payment received"} ·{" "}
                {new Intl.NumberFormat("en-AU", { style: "currency", currency: (info.currency || "aud").toUpperCase(), maximumFractionDigits: Number.isInteger(info.amount) ? 0 : 2 }).format(info.amount)}
              </p>
            )}
            <p className="mt-4 text-white/70 text-sm leading-relaxed max-w-md mx-auto">
              Thank you — we've emailed your receipt. A member of our team will be in touch within 24
              business hours to confirm your onboarding.
            </p>
            <Link to="/" data-testid="payment-home"
              className="mt-8 inline-flex items-center gap-2 bg-teal text-white font-display font-semibold text-sm rounded-sm px-6 py-3.5 hover:bg-[#08929f] transition-colors">
              Return to the program
            </Link>
          </div>
        )}

        {(state === "timeout" || state === "error" || state === "expired") && (
          <div data-testid="payment-issue">
            <XCircle className="h-12 w-12 text-white/60 mx-auto" />
            <h1 className="mt-6 font-display font-extrabold text-white text-2xl tracking-tight">
              {state === "expired" ? "This payment did not complete." : "We're still confirming your payment."}
            </h1>
            <p className="mt-4 text-white/70 text-sm leading-relaxed max-w-md mx-auto">
              {state === "expired"
                ? "The checkout session expired or was cancelled. You can try again from the enrolment section."
                : "If you completed payment, your receipt will arrive by email shortly. Please contact us if you have any questions."}
            </p>
            <Link to="/" data-testid="payment-home"
              className="mt-8 inline-flex items-center gap-2 border border-hair-dark text-white font-display font-semibold text-sm rounded-sm px-6 py-3.5 hover:bg-white/5 transition-colors">
              <CalendarDays className="h-4 w-4" /> Return to the program
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
